// axios
import axios from 'axios'
import Vue from 'vue'
import store from '@/store'

const axiosIns = axios.create({
  // You can add your headers here
  // ================================
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
})

axiosIns.interceptors.request.use(
  config => {
    // Do something before request is sent
    const sessionData = localStorage.getItem('session') || sessionStorage.getItem('session')
    const parsedData = JSON.parse(sessionData)
    const accessToken = parsedData?.access

    // eslint-disable-next-line no-param-reassign
    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
    // console.log(parsedData)

    return config
  },
  error => Promise.reject(error),
)

const getStoredSession = () => {
  const sessionData = localStorage.getItem('session') || sessionStorage.getItem('session')
  return sessionData ? JSON.parse(sessionData) : null
}

const clearStoredSession = () => {
  sessionStorage.removeItem('session')
  localStorage.removeItem('session')
}

const persistRefreshedTokens = ({ access, refresh }) => {
  const session = getStoredSession()
  if (!session) return
  session.access = access
  if (refresh) session.refresh = refresh
  const storage = localStorage.getItem('session') ? localStorage : sessionStorage
  storage.setItem('session', JSON.stringify(session))
}

const forceLogout = () => {
  clearStoredSession()
  window.location.replace('login')
}

// Access tokens are short-lived (see back/pi-tools/settings.py SIMPLE_JWT).
// On a 401 we transparently swap the refresh token for a new access token
// and retry the original request, instead of forcing a re-login every time
// the access token expires. `isRefreshing`/`pendingQueue` collapse concurrent
// 401s (e.g. several requests in flight at once) into a single refresh call.
let isRefreshing = false
let pendingQueue = []

axiosIns.interceptors.response.use(response => {
  // A real server response means we can reach the backend → we are online.
  if (!store.getters['offline/isOnline']) store.commit('offline/SET_ONLINE', true)

  return response;
}, error => {
  const { config, response } = error
  // No `response` = the request never reached the server (network error / timeout).
  // navigator.onLine lies on a LAN with no internet, so backend reachability is the
  // source of truth. Confirm with a probe instead of flipping on a single aborted
  // request (avoids a jarring redirect on a one-off blip).
  if (!response) {
    store.dispatch('offline/checkReachability')
  }
  // back's custom_exception_handler (back/back/utils/exceptions.py) rewrites
  // DRF's default_code into `key`, not `code` — simplejwt's InvalidToken
  // exception has default_code="token_not_valid", so that's what arrives here.
  const isAuthError = response?.data?.key === 'token_not_valid'

  // The refresh call itself failed (refresh token expired/blacklisted) —
  // let the caller (below) handle logout, don't recurse into refresh logic.
  if (config?._isRefreshCall) return Promise.reject(error)

  if (!isAuthError) return Promise.reject(error)

  // Already retried once with a fresh token and still failing — give up.
  if (config._retry) {
    forceLogout()
    return Promise.reject(error)
  }
  config._retry = true

  const refreshToken = getStoredSession()?.refresh
  if (!refreshToken) {
    forceLogout()
    return Promise.reject(error)
  }

  if (!isRefreshing) {
    isRefreshing = true

    return axiosIns.post('api/account/token/refresh', { refresh: refreshToken }, { _isRefreshCall: true })
      .then(({ data }) => {
        persistRefreshedTokens(data)
        pendingQueue.forEach(resolve => resolve(data.access))
        pendingQueue = []
        config.headers.Authorization = `Bearer ${data.access}`

        return axiosIns(config)
      })
      .catch(refreshError => {
        pendingQueue.forEach(resolve => resolve(null))
        pendingQueue = []
        forceLogout()
        throw refreshError
      })
      .finally(() => {
        isRefreshing = false
      })
  }

  // A refresh is already in flight — wait for it and retry with its result.
  return new Promise((resolve, reject) => {
    pendingQueue.push(newAccessToken => {
      if (!newAccessToken) return reject(error)
      config.headers.Authorization = `Bearer ${newAccessToken}`
      resolve(axiosIns(config))
    })
  })
});

//logIn
axiosIns.login = form => {
  return axiosIns.post(`api/account/login`, form)
    .then(response => response.data)
}

// Blacklists the refresh token server-side so it can't be reused after logout.
axiosIns.logoutUser = refreshToken => axiosIns.post(`api/account/logout`, { refresh: refreshToken })
  .then(response => response.data)

//logged-UserData
axiosIns.getUserDataForProfile = () => axiosIns.get(`api/user_information_profile/`)
  .then(response => response.data)

// Update Profile
axiosIns.updateUserDataForProfile = form => axiosIns.patch(`api/user_information_profile/`, form) /* CORREGIR */
  .then(response => response.data)

// Update Profile Photo
axiosIns.updateUserPhoto = formData => axiosIns.patch(`api/user_information_profile/`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
}).then(response => response.data)

axiosIns.changeUserPasswordProfile = form => axiosIns.patch(`api/account/password/change`, form)
  .then(response => response.data)

axiosIns.sendResetPasswordEmail = form => axiosIns.post(`api/account/password/reset`, form)
  .then(response => response.data)

axiosIns.validateToken = form => axiosIns.post(`api/account/password/validate`, form)
  .then(response => response)

axiosIns.recoverPassword = form => axiosIns.post(`api/account/password/confirm`, form)
  .then(response => response)

// Users EPs
axiosIns.createUser = form => axiosIns.post(`api/account/register`, form)
  .then(response => response.data)

axiosIns.getUsersList = (data) => {
  let query = '?'

  if (data?.page && data?.page_size) query +=
    query.length >1?
      `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

  if (data?.search && data?.search.length) query +=
    query.length >1?
      `&search=${data.search}` : `search=${data.search}`

  if (data?.role !== undefined && data?.role !== -1) query +=
    query.length > 1?
      `&role=${data.role}` : `role=${data.role}`

  if (data?.summary) query +=
    query.length > 1?
      '&summary=true&paginated=false' : 'summary=true&paginated=false'

  if (data?.country && data?.country !== -1 && data?.country !== '-') query +=
    query.length >1?
      `&country__name=${data.country}` : `country__name=${data.country}`

  if (data?.order) {
    query += query.length >1
      ? `&order=${data.order}`
      : `order=${data.order}`
  }

  return axiosIns.get(`api/user_information/${query.length > 1? query : ''}`)
    .then(response => response.data)
}

axiosIns.getUser = id => axiosIns.get(`api/user_information/${id}/`)
  .then(response => response)

axiosIns.updateUser = (id,form) => axiosIns.patch(`api/user_information/${id}/`, form)
  .then(response => response.data)

axiosIns.deleteUser = id => axiosIns.delete(`api/user_information/${id}/`)
  .then(response => response)

// Country EPs

axiosIns.getCountryList = (data) => {
  let query = '?'
  if (data.paginated === false) {
    query += 'paginated=false'
    if (data?.without_region) query += '&without_region=true'
    if (data?.region && data?.region !== -1 && data?.region !== '-') query += `&region=${data.region}`
    if (data?.has_evaluations) query += '&has_evaluations=true'
  }
  else {

    if (data?.page && data?.page_size) query +=
      query.length >1?
        `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

    if (data?.search && data?.search.length) query +=
      query.length >1?
        `&search=${data.search}` : `search=${data.search}`

    if (data?.region && data?.region !== -1 && data?.region !== '-') query +=
      query.length >1?
        `&region=${data.region}` : `region=${data.region}`

    if (data?.order) {
      query += query.length >1
        ? `&order=${data.order}`
        : `order=${data.order}`
    }
  }

  return axiosIns.get(`api/country/${query.length > 1? query : ''}`)
    .then(response => response.data)
}

//Zone EPs

axiosIns.getZoneList = (data) => {
  let query = '?'

  if (data.paginated === false) {
    query += 'paginated=false'
    if (data?.country && data?.country !== -1 && data?.country !== '-') query += `&country__name=${data.country}`
    if (data?.region && data?.region !== -1 && data?.region !== '-') query += `&region=${data.region}`
    if (data?.search && data?.search.length) query += `&search=${data.search}`
    if (data?.has_evaluations) query += '&has_evaluations=true'
  }
  else {
    if (data?.page && data?.page_size) query +=
      query.length >1?
        `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

    if (data?.search && data?.search.length) query +=
      query.length >1?
        `&search=${data.search}` : `search=${data.search}`

    if (data?.summary) query +=
      query.length > 1?
        '&summary=true&paginated=false' : 'summary=true&paginated=false'

    if (data?.country && data?.country !== -1 && data?.country !== '-') query +=
      query.length >1?
        `&country__name=${data.country}` : `country__name=${data.country}`

    if (data?.region && data?.region !== -1 && data?.region !== '-') query +=
      query.length >1?
        `&region=${data.region}` : `region=${data.region}`

    if (data?.order) {
      query += query.length >1
        ? `&order=${data.order}`
        : `order=${data.order}`
    }
  }

  return axiosIns.get(`api/zone/${query.length > 1? query : ''}`)
    .then(response => response.data)
}

axiosIns.createZone = (form) => axiosIns.post(`api/zone/`,form)
  .then(response => response.data)

axiosIns.updateZone = (id, form) => axiosIns.patch(`api/zone/${id}/`,form)
  .then(response => response.data)

axiosIns.getZone = (id) => axiosIns.get(`api/zone/${id}/`)
  .then(response => response.data)

axiosIns.deleteZone = (id) => axiosIns.delete(`api/zone/${id}/`)
  .then(response => response.data)

//Region EPs (country grouping — admin only)

axiosIns.getRegionList = (data = {}) => {
  let query = '?'

  if (data.paginated === false) {
    query += 'paginated=false'
    if (data?.search && data?.search.length) query += `&search=${data.search}`
    if (data?.has_evaluations) query += '&has_evaluations=true'
  }
  else {
    if (data?.page && data?.page_size) query +=
      query.length >1?
        `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

    if (data?.search && data?.search.length) query +=
      query.length >1?
        `&search=${data.search}` : `search=${data.search}`

    if (data?.order) {
      query += query.length >1
        ? `&order=${data.order}`
        : `order=${data.order}`
    }
  }

  return axiosIns.get(`api/region/${query.length > 1? query : ''}`)
    .then(response => response.data)
}

axiosIns.createRegion = (form) => axiosIns.post(`api/region/`,form)
  .then(response => response.data)

axiosIns.updateRegion = (id, form) => axiosIns.patch(`api/region/${id}/`,form)
  .then(response => response.data)

axiosIns.getRegion = (id) => axiosIns.get(`api/region/${id}/`)
  .then(response => response.data)

axiosIns.deleteRegion = (id) => axiosIns.delete(`api/region/${id}/`)
  .then(response => response.data)
//Area EPs

axiosIns.addArea = (form) => axiosIns.post(`api/area/`, form)
  .then(response => {
    return response.data
  }
  )

axiosIns.removeArea = (id) => axiosIns.delete(`api/area/${id}/`)
  .then(response => response.data)

//School EPs
axiosIns.getSchoolList = (data) => {
  let query = '?'
  if (data.paginated === false) {
    query += 'paginated=false'
    if (data?.zone && data?.zone !== -1 && data?.zone !== '-') query += `&zone__name=${data.zone}`
    if (data?.country && data?.country !== -1 && data?.country !== '-') query += `&country__name=${data.country}`
    if (data?.region && data?.region !== -1 && data?.region !== '-') query += `&region=${data.region}`
    if (data?.search && data?.search.length) query += `&search=${data.search}`
    if (data?.has_evaluations) query += '&has_evaluations=true'
  }
  else {
    if (data?.page && data?.page_size) query +=
      query.length >1?
        `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

    if (data?.search && data?.search.length) query +=
      query.length >1?
        `&search=${data.search}` : `search=${data.search}`

    if (data?.summary) query +=
      query.length > 1?
        '&summary=true&paginated=false' : 'summary=true&paginated=false'

    if (data?.country && data?.country !== -1 && data?.country !== '-') query +=
      query.length >1?
        `&country__name=${data.country}` : `country__name=${data.country}`

    if (data?.zone && data?.zone !== -1 && data?.zone !== '-') query +=
      query.length >1?
        `&zone__name=${data.zone}` : `zone__name=${data.zone}`

    if (data?.region && data?.region !== -1 && data?.region !== '-') query +=
      query.length >1?
        `&region=${data.region}` : `region=${data.region}`

    if (data?.order) {
      query += query.length >1
        ? `&order=${data.order}`
        : `order=${data.order}`
    }
  }

  return axiosIns.get(`api/school/${query.length > 1? query : ''}`)
    .then(response => response.data)
}

axiosIns.getSchool = (id) => axiosIns.get(`api/school/${id}/`)
  .then(response => response.data)

axiosIns.createSchool = (form) => axiosIns.post(`api/school/`,form)
  .then(response => response.data)

axiosIns.updateSchool = (id, form) => axiosIns.patch(`api/school/${id}/`,form)
  .then(response => response.data)

axiosIns.deleteSchool = (id) => axiosIns.delete(`api/school/${id}/`)
  .then(response => response.data)

axiosIns.rolesForCreate = () => axiosIns.get(`api/roles_for_create/`)
  .then(response => response.data)


// Notifications EPs
axiosIns.getNotifications = (query) => {
  let q = '?'

  if (query?.page && query?.page_size) q +=
    q.length >1 ?
      `&page=${query.page}&page_size=${query.page_size}` : `page=${query.page}&page_size=${query.page_size}`

  if (query?.search && query?.search.length) q +=
    q.length >1 ?
      `&search=${query.search}` : `search=${query.search}`


  return axiosIns.get(`api/notifications/${q.length > 1 ? q : ''}`)
    .then(response => response.data)
}

axiosIns.sendNotification = (form) => axiosIns.post(`api/notifications/`, form)
  .then(response => response.data)

axiosIns.markNotificationAsRead = (id) => axiosIns.post(`api/notifications/${id}/mark_as_read/`)
  .then(response => response.data)

axiosIns.readAllNotifications = () => axiosIns.post(`api/notifications/mark_all_as_read/`)
  .then(response => response.data)

// Evaluation EPs
axiosIns.getEvaluationList = (data) => {
  let query = '?'

  if (data?.page && data?.page_size) query +=
    query.length >1?
      `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

  if (data?.search && data?.search.length) query +=
    query.length >1?
      `&search=${data.search}` : `search=${data.search}`

  if (data?.summary) query +=
    query.length > 1?
      '&summary=true&paginated=false' : 'summary=true&paginated=false'

  if (data?.country && data?.country !== -1 && data?.country !== '-') query +=
    query.length >1?
      `&country__name=${data.country}` : `country__name=${data.country}`

  if (data?.zone && data?.zone !== -1 && data?.zone !== '-') query +=
    query.length >1?
      `&zone__name=${data.zone}` : `zone__name=${data.zone}`

  if (data?.region && data?.region !== -1 && data?.region !== '-') query +=
    query.length >1?
      `&region=${data.region}` : `region=${data.region}`

  if (data?.year && data?.year !== -1 && data?.year !== '-') query +=
    query.length >1?
      `&evaluation_line__year=${data.year}` : `evaluation_line_year=${data.year}`

  if (data?.status && data?.status !== -1 && data?.status !== '-') query +=
    query.length >1?
      `&general_status=${encodeURIComponent(data.status)}` : `general_status=${encodeURIComponent(data.status)}`

  if (data?.school) query +=
    query.length >1?
      `&school=${data.school}` : `school=${data.school}`

  if (data?.is_downloaded_offline) query +=
    query.length >1?
      '&is_downloaded_offline=true' : 'is_downloaded_offline=true'

  return axiosIns.get(`api/evaluation/${query.length > 1? query : ''}`)
    .then(response => response.data)
}

axiosIns.getEvaluation = (params) => {

  let query = '?'
  if (params?.evaluation_line && params?.evaluation_line !== -1 && params?.evaluation_line !== '-') query +=
    query.length >1?
      `&evaluation_line_id=${params.evaluation_line}` : `evaluation_line_id=${params.evaluation_line}`

  return axiosIns.get(`api/evaluation/${params.id}/${query.length > 1? query : ''}`)
    .then(response => response.data)
}

axiosIns.createEvaluation = (form) => axiosIns.post(`api/evaluation/`, form)
  .then(response => response)

axiosIns.deleteEvaluation = (id) => axiosIns.delete(`/api/evaluation/${id}/`)
  .then(response => response)

axiosIns.duplicateEvaluation = (id) => axiosIns.post(`/api/evaluation/${id}/duplicate/`, {}, { timeout: 30000 })
  .then(response => response.data)

axiosIns.changeEvaluationDate = (id, form) => axiosIns.patch(`api/evaluation/${id}/`, form)
  .then(response => response)

axiosIns.updateIndiPrin = (form) => axiosIns.patch(`api/evaluation/indiprin/`, form)
  .then(response => response)

// Every option of every instrument (a handful): for resolving an option id
// alone, e.g. in an archived offline payload.
axiosIns.getAllOptions = () => axiosIns.get('/api/option/?paginated=false')
  .then(response => response.data)

axiosIns.getOptionswithScores = (id) => axiosIns.get(`/api/option/?instrument_id=${id}`)
  .then(response => response.data)

axiosIns.editOptionScore = (id, form) => axiosIns.patch(`/api/option/${id}/`, form)
  .then(response => response.data)

axiosIns.changePeriod = (id, form) => axiosIns.patch(`/api/evaluation/${id}/`, form)
  .then(response => response.data)

axiosIns.getReviewReadiness = (id, evaluationLineId) => axiosIns.get(`api/evaluation-stage-progress/${id}/check-review-readiness/`, {
  params: evaluationLineId ? { evaluation_line_id: evaluationLineId } : {},
})
  .then(response => response.data)

axiosIns.getMissingComments = (id, evaluationLineId) => axiosIns.get(`api/evaluation-stage-progress/${id}/missing-comments/`, {
  params: evaluationLineId ? { evaluation_line_id: evaluationLineId } : {},
})
  .then(response => response.data)

axiosIns.getMissingResults = (id, evaluationLineId) => axiosIns.get(`api/evaluation-stage-progress/${id}/missing-results/`, {
  params: evaluationLineId ? { evaluation_line_id: evaluationLineId } : {},
})
  .then(response => response.data)

axiosIns.getMissingEvidence = (id, evaluationLineId) => axiosIns.get(`api/evaluation-stage-progress/${id}/missing-evidence/`, {
  params: evaluationLineId ? { evaluation_line_id: evaluationLineId } : {},
})
  .then(response => response.data)

axiosIns.submitForReview = (id, form) => axiosIns.post(`/api/evaluation-stage-progress/${id}/submit-for-review/`, form)
  .then(response => response.data)

axiosIns.validateReview = (id, form) => axiosIns.post(`/api/evaluation-stage-progress/${id}/review/`, form)
  .then(response => response.data)

axiosIns.getStageReviewers = (id) => axiosIns.get(`/api/evaluation-stage-progress/${id}/reviewers/`)
  .then(response => response.data)

axiosIns.checkActionPlanReviewReadiness = (id) => axiosIns.get(`/api/review-action-plan/${id}/check-review-readiness/`)
  .then(response => response.data)

axiosIns.sendActionPlanToReview = (id, form) => axiosIns.post(`/api/review-action-plan/${id}/submit-for-review/`, form)
  .then(response => response.data)

axiosIns.validateActionPlanReview = (id, form) => axiosIns.post(`/api/review-action-plan/${id}/review/`, form)
  .then(response => response.data)

axiosIns.getActionPlanActivityLog = (actionPlanId) => axiosIns.get(`/api/review-action-plan/${actionPlanId}/activity-log/`)
  .then(response => response.data)

axiosIns.cancelStageReview = (id, stageReviewIds) => axiosIns.post(
  `/api/evaluation-stage-progress/${id}/cancel-review/`,
  stageReviewIds && stageReviewIds.length ? { stage_review_ids: stageReviewIds } : {}
).then(response => response.data)

axiosIns.invalidateStage = (id, stageReviewIds) => axiosIns.post(
  `/api/evaluation-line/${id}/invalidate/`,
  { stage_review_ids: stageReviewIds }
).then(response => response.data)

// Offline sync: uploads the local batch (scores/comments/demographics) for an
// evaluation line. `id` is the evaluation_line id (the URL pk). The server
// applies every item, releases the offline-download lock, and returns a
// per-item summary/errors report (see back sync_offline.perform_offline_sync).
// If the user no longer holds the lock (released by an admin, QIP deleted...)
// nothing is applied: the back archives the batch for admin review and answers
// 200 with `applied: false` (key `offline_changes_archived_not_applied`).
axiosIns.syncOffline = (id, form) => axiosIns.post(`/api/evaluation-line/${id}/sync-offline/`, form)
  .then(response => response.data)

axiosIns.cancelActionPlanReview = (id) => axiosIns.post(`/api/review-action-plan/${id}/cancel-review/`)
  .then(response => response.data)

axiosIns.reactivateActionPlan = (id) => axiosIns.post(`/api/review-action-plan/${id}/reactivate/`)
  .then(response => response.data)

axiosIns.openLine = (id, form) => axiosIns.post(`/api/evaluation-stage-progress/${id}/open/`, form)
  .then(response => response.data)

axiosIns.getActivityLog = (evaluationId, params = {}) => axiosIns.get(`/api/evaluation/${evaluationId}/activity-log/`, { params })
  .then(response => response.data)

axiosIns.updateYear = (id, form) => axiosIns.patch(`/api/evaluation-line/${id}/update-year/`, form)
  .then(response => response.data)

axiosIns.closeLine = (id, form) => axiosIns.post(`/api/evaluation-line/${id}/closed/`, form)
  .then(response => response.data)

axiosIns.closeLineWithoutActionPlan = (id) => axiosIns.post(`/api/evaluation-line/${id}/close-without-action-plan/`)
  .then(response => response.data)

axiosIns.openEvaluationLine = (id) => axiosIns.post(`/api/evaluation-line/${id}/open/`)
  .then(response => response.data)

axiosIns.deleteEvaluationLine = (id) => axiosIns.delete(`/api/evaluation-line/${id}/`)
  .then(response => response.data)

axiosIns.reopenEvaluationLine = (id) => axiosIns.post(`/api/evaluation-line/${id}/reopen/`)
  .then(response => response.data)

// Offline checkout: marks the line as downloaded-for-offline (claims the lock).
// Does NOT return a snapshot — the front builds that client-side from existing GETs.
axiosIns.downloadOfflineLine = (id) => axiosIns.post(`/api/evaluation-line/${id}/download-offline/`)
  .then(response => response.data)

// Lines the back currently has checked out offline by the session user. Used to
// drop local copies whose lock no longer exists server-side.
axiosIns.getMyOfflineLines = () => axiosIns.get('/api/evaluation-line/my-offline/')
  .then(response => response.data)

// Admin only: takes the offline lock away from whoever holds the line. The back
// notifies the holder (in-app + email); their later sync is archived, not applied.
axiosIns.releaseOfflineLine = (id) => axiosIns.post(`/api/evaluation-line/${id}/release-offline/`)
  .then(response => response.data)

// Tells the back that this browser had a copy of a released line with no
// pending changes, so it stops reminding the user to sync it.
axiosIns.dismissPendingSync = (id) => axiosIns.post(`/api/evaluation-line/${id}/dismiss-pending-sync/`)
  .then(response => response.data)

// Archives (without applying) the local changes of a copy about to be wiped
// after losing its lock. Same body as sync-offline.
axiosIns.discardOfflineLine = (id, form) => axiosIns.post(`/api/evaluation-line/${id}/offline-discarded/`, form)
  .then(response => response.data)

// Admin only: archive of offline batches (SYNCED / DISCARDED / REPLAYED).
// Filters: evaluation, evaluation_line, user (auth user id), source.
axiosIns.getOfflineSyncPayloads = (params) => axiosIns.get('/api/offline-sync-payloads/', { params })
  .then(response => response.data)

axiosIns.getOfflineSyncPayload = (id) => axiosIns.get(`/api/offline-sync-payloads/${id}/`)
  .then(response => response.data)

// Re-applies an archived batch onto its line, overwriting current scores and
// demographics. The back refuses without `confirm_overwrite: true`.
axiosIns.replayOfflineSyncPayload = (id) => axiosIns.post(`/api/offline-sync-payloads/${id}/replay/`, { confirm_overwrite: true })
  .then(response => response.data)

axiosIns.finalizeEvaluation = (id) => axiosIns.post(`/api/evaluation/${id}/finalize/`)
  .then(response => response.data)

axiosIns.reopenEvaluation = (id) => axiosIns.post(`/api/evaluation/${id}/reopen/`)
  .then(response => response.data)

axiosIns.generateEvaluationReport = (id, payload) => axiosIns.post(`/api/evaluation/${id}/generate-report/`, payload)
  .then(response => response.data)

axiosIns.getEvaluationGenerationTasks = (id) => axiosIns.get(`/api/evaluation/${id}/generation-tasks/`)
  .then(response => response.data)

axiosIns.getGenerationTask = (taskId) => axiosIns.get(`/api/generation-tasks/${taskId}/`)
  .then(response => response.data)

axiosIns.downloadGenerationTask = (taskId) => axiosIns.get(`/api/generation-tasks/${taskId}/download/`, { responseType: 'arraybuffer' })

axiosIns.unlockEvaluation = (id) => axiosIns.patch(`/api/evaluation/${id}/unlock/`)
  .then(response => response.data)

axiosIns.validateIncompleteActionPlan = (id) => axiosIns.post(`/api/review-action-plan/${id}/validate_super_admin/`)
  .then(response => response.data)

// Instruments EPs

axiosIns.getInstrumentList = () => axiosIns.get(`api/instruments/`)
  .then(response => response.data)

axiosIns.toggleInstrumentActive = (id) => axiosIns.post(`api/instruments/${id}/toggle_enabled/`)
  .then(response => response.data)

axiosIns.getInstrument = (id) => axiosIns.get(`api/instruments/${id}/`)
  .then(response => response.data)

axiosIns.postResult = (form) => axiosIns.post(`api/result/`, form)
  .then(response => response)

axiosIns.updateResult = (form, id) => axiosIns.patch(`api/result/${id}/`, form)
  .then(response => response)

axiosIns.deleteResult = (id) => axiosIns.delete(`api/result/${id}/`)
  .then(response => response)

axiosIns.uploadFile = (form) => axiosIns.post(`api/file/`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 20000 })
  .then(response => response)

axiosIns.removeFile = (id) => axiosIns.delete(`api/file/${id}/`)
  .then(response => response)

axiosIns.updateFile = (id, form) => axiosIns.patch(`api/file/${id}/`, form)
  .then(response => response)

axiosIns.uploadLink = (form) => axiosIns.post(`api/link/`, form)
  .then(response => response)

axiosIns.removeLink = (id) => axiosIns.delete(`api/link/${id}/`)
  .then(response => response)

axiosIns.downloadFile = (id) => axiosIns.get(`api/file/${id}/`, {responseType: 'arraybuffer',})
  .then(response => response)

axiosIns.addComment = (form) => axiosIns.post(`api/comment/`, form)
  .then(response => response)

axiosIns.removeComment = (id) => axiosIns.delete(`api/comment/${id}/`)
  .then(response => response)

axiosIns.editComment = (id, form) => axiosIns.patch(`api/comment/${id}/`, form)
.then(response => response)

// Results EPs

axiosIns.getResults = (id, params) => axiosIns.get(`api/result/${id}/`, { params })
  .then(response => response.data)
axiosIns.getNote = (id) => axiosIns.get(`api/note_evaluation/${id}/`)
  .then(response => response)

axiosIns.createNote = (form) => axiosIns.post(`api/note/`, form)
  .then(response => response)

axiosIns.editNote = (id, form) => axiosIns.patch(`api/note/${id}/`, form)
  .then(response => response)

axiosIns.deleteNote = (id) => axiosIns.delete(`api/note/${id}/`)
  .then(response => response)

axiosIns.getSchoolResults = (id) => axiosIns.get(`api/results_school/${id}/`, { timeout: 20000 })
  .then(response => response.data)

axiosIns.createGoalsAndResult = (params) => axiosIns.post(`api/action_plan/${params.action_plan}/goals/`, params)
  .then(response => response)

axiosIns.deleteGoalsAndResult = (params) => axiosIns.delete(`api/action_plan/${params.action_plan}/goals/${params.goal}/`)
  .then(response => response)

axiosIns.updateGoalsAndResult = (params, form) => axiosIns.patch(`api/action_plan/${params.action_plan}/goals/${params.goal}/`, form)
  .then(response => response)

axiosIns.createQuestion = (form) => axiosIns.post(`api/questions/`, form)
  .then(response => response)

axiosIns.editQuestion = (params, form) => axiosIns.patch(`api/action_plan/${params.action_plan}/questions/${params.question}/`, form)
  .then(response => response)

axiosIns.createObservation = (params, form) => axiosIns.post(`api/goals/${params.goal}/observations/`, form)
  .then(response => response)

axiosIns.updateObservation = (params, form) => axiosIns.patch(`api/goals/${params.goal}/observations/${params.observation}/`, form)
  .then(response => response)

axiosIns.deleteObservation = params => axiosIns.delete(`api/goals/${params.goal}/observations/${params.observation}/`)
  .then(response => response)

axiosIns.createAction = (params, form) => axiosIns.post(`api/goals/${params.goal}/actions/`, form)
  .then(response => response)

axiosIns.updateAction = (params, form) => axiosIns.patch(`api/goals/${params.goal}/actions/${params.action}/`, form)
  .then(response => response)

axiosIns.deleteAction = (params) => axiosIns.delete(`api/goals/${params.goal}/actions/${params.action}/`)
  .then(response => response)

// Action plan links (implementation phase, VALIDATED)
axiosIns.createActionPlanLink = (actionPlanId, form) => axiosIns.post(`api/action_plan/${actionPlanId}/links/`, form)
  .then(response => response.data)

axiosIns.deleteActionPlanLink = (actionPlanId, pk) => axiosIns.delete(`api/action_plan/${actionPlanId}/links/${pk}/`)
  .then(response => response)

// Action (activity) comments (implementation phase, VALIDATED) — create + edit, no delete
axiosIns.createActionComment = (actionId, form) => axiosIns.post(`api/actions/${actionId}/comments/`, form)
  .then(response => response.data)

axiosIns.updateActionComment = (actionId, pk, form) => axiosIns.patch(`api/actions/${actionId}/comments/${pk}/`, form)
  .then(response => response.data)

// Validate whether a validated action plan is ready to be closed (region admin / admin)
// 200 -> ready; 400 -> { errors: [{ key, message }] }
axiosIns.validateActionPlanForClose = (id) => axiosIns.get(`/api/review-action-plan/${id}/validate-for-close/`)
  .then(response => response.data)

// Close a validated action plan (region admin / admin)
axiosIns.closeActionPlan = (id) => axiosIns.post(`/api/review-action-plan/${id}/close/`)
  .then(response => response.data)

axiosIns.getActionPlan = (params) => axiosIns.get(`api/evaluation/${params.id}/action_plan/${params.evaluation_line ? `?evaluation_line_id=${params.evaluation_line}` : ''}`)
  .then(response => response.data)

axiosIns.getSchoolResults = (id, evaluationId = null) => axiosIns.get(`api/results_school/${id}/`, { params: evaluationId ? { evaluation_id: evaluationId } : {}, timeout: 20000 })
  .then(response => response.data)

axiosIns.getDomainsAndOutcomes = (id) => axiosIns.get(`api/instruments/${id}/categories/?short=true`)
  .then(response => response.data)

axiosIns.unlockActionPlan = (id) => axiosIns.patch(`api/action_plan/${id}/unlock/`)
  .then(response => response.data)

// axiosIns.updateActionPlan = (id, form) => axiosIns.patch(`api/action_plan/${id}/`, form)
//   .then(response => response.data)

// axiosIns.deleteActionPlan = (id) => axiosIns.delete(`api/action_plan/${id}/`)
//   .then(response => response.data)

// REPORTS EPs

axiosIns.getReports = (data) => {
  let query = '?'

  if (data?.page && data?.page_size) query +=
    query.length >1?
      `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

  return axiosIns.get(`api/report/${data.country ? 'country' : 'zone'}/${data.id}/${query.length > 1? query : ''}`)
    .then(response => response.data)
  }

axiosIns.editReport = (form, id) => axiosIns.patch(`api/report/${id}/`, form)
  .then(response => response.data)

axiosIns.deleteReport = (id) => axiosIns.delete(`api/report/${id}/`)
  .then(response => response.data)

axiosIns.createReport = (form, data) => axiosIns.post(`api/report/${data.isCountry ? 'country' : 'zone'}/${data.id}/`, form)
  .then(response => response.data)

// REPORT TASKS EPs (new report system — Program/Group reports + gallery)

axiosIns.generateProgramReport = (payload) => axiosIns.post(`api/reports/program/`, payload)
  .then(response => response.data)

axiosIns.generateGroupReport = (payload) => axiosIns.post(`api/reports/group/`, payload)
  .then(response => response.data)

axiosIns.getReportTasks = (params = {}) => axiosIns.get(`api/report-tasks/`, { params })
  .then(response => response.data)

axiosIns.getReportTask = (id) => axiosIns.get(`api/report-tasks/${id}/`)
  .then(response => response.data)

// Returns the full response (not .data) — consumer builds the Blob, like downloadGenerationTask.
// Query param is 'file_format', not 'format' — DRF reserves 'format' for content negotiation.
axiosIns.downloadReportTask = (id, format) => axiosIns.get(`api/report-tasks/${id}/download/`, {
  params: { file_format: format },
  responseType: 'arraybuffer',
})

axiosIns.deleteReportTask = (id) => axiosIns.delete(`api/report-tasks/${id}/`)
  .then(response => response.data)

// Creates a new ReportTask with this one's exact saved params (new row in the gallery).
axiosIns.regenerateReportTask = (id) => axiosIns.post(`api/report-tasks/${id}/regenerate/`)
  .then(response => response.data)

// STATISCTICS EPs

axiosIns.getEvaluationStatistics = (params = {}) => axiosIns.get(`api/statistics/evaluations`, { params })
  .then(response => response.data)

axiosIns.getSchoolStatistics = (params = {}) => axiosIns.get(`api/statistics/schools`, { params })
  .then(response => response.data)

// Student Demographics EPs (Evaluation Line level)
axiosIns.getStudentDemographics = evaluationLineId => axiosIns.get(`api/evaluation-line-students/${evaluationLineId}/`)
  .then(response => response.data)

axiosIns.updateStudentDemographics = (evaluationLineId, form) => axiosIns.patch(`api/evaluation-line-students/${evaluationLineId}/`, form)
  .then(response => response.data)

// History EPs
axiosIns.getHistoryList = (data) => {
  let query = '?'

  if (data?.page && data?.page_size) query +=
    query.length > 1 ?
      `&page=${data.page}&page_size=${data.page_size}` : `page=${data.page}&page_size=${data.page_size}`

  if (data?.search && data?.search.length) query +=
    query.length > 1 ?
      `&search=${data.search}` : `search=${data.search}`

  if (data?.model) query +=
    query.length > 1 ?
      `&model=${data.model}` : `model=${data.model}`

  if (data?.object_id) query +=
    query.length > 1 ?
      `&object_id=${data.object_id}` : `object_id=${data.object_id}`

  if (data?.actor) query +=
    query.length > 1 ?
      `&actor=${data.actor}` : `actor=${data.actor}`

  if (data?.action) query +=
    query.length > 1 ?
      `&action=${data.action}` : `action=${data.action}`

  if (data?.date_from) query +=
    query.length > 1 ?
      `&date_from=${data.date_from}` : `date_from=${data.date_from}`

  if (data?.date_to) query +=
    query.length > 1 ?
      `&date_to=${data.date_to}` : `date_to=${data.date_to}`

  if (data?.school_id) query +=
    query.length > 1 ?
      `&school_id=${data.school_id}` : `school_id=${data.school_id}`

  return axiosIns.get(`api/history/${query.length > 1 ? query : ''}`)
    .then(response => response.data)
}

Vue.prototype.$api = axiosIns

export default axiosIns
